import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import type { AccountRow } from '../../lib/contract'
import styles from './Accounts.module.css'

/**
 * Everyone who has signed up, and the one act that matters: letting someone play.
 *
 * Signing up is not gated, so this is no longer a queue — most rows are people
 * who made an account and are browsing perfectly happily. Approval is a separate
 * decision about the ENGINE, which is the thing that costs money to run.
 *
 * Ships in the production bundle, so it is worth nothing on its own — every
 * endpoint it calls is admin-checked server-side.
 */
/**
 * What one press grants, in each currency's smallest **payable** unit.
 *
 * ⚠ ISK HAS NO SUB-UNIT — Iceland retired the eyrir in 2003, so 500 here is five hundred krónur,
 * where 500 in USD is five dollars. Roughly $3.70 against $5.00: near enough for a testing budget,
 * and both are round numbers in their own money, which is the whole posture of the price list.
 */
const AMOUNT: Record<'ISK' | 'USD', number> = { ISK: 500, USD: 500 }

const label = (c: 'ISK' | 'USD') => (c === 'ISK' ? '500 kr' : '$5')

export default function Accounts() {
  const [rows, setRows] = useState<AccountRow[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')

  /*
   * WHICH CURRENCY A TOP-UP IS MADE IN.
   *
   * ⚠ IT MATTERS PERMANENTLY, WHICH IS WHY IT IS A CONTROL AND NOT A CONSTANT. The first credit
   * FIXES an account's currency; every one after it must match or is refused. Most testers are
   * Icelandic, so getting this wrong once means a wallet stuck on the wrong price list.
   *
   * Per-page rather than per-row: you top up a batch of testers in one sitting, and they are
   * almost always the same market.
   */
  const [currency, setCurrency] = useState<'ISK' | 'USD'>('ISK')

  const load = useCallback(async () => {
    try {
      setRows(await api.listAccounts())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the accounts.')
      setRows([])
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Put money in an account's wallet, by hand.
   *
   * ⚠ EVERY CLICK IS A SEPARATE GRANT, deliberately. The server generates a fresh idempotency key
   * when the caller sends none, so two taps are $10 — which is what a button reading "+ $5"
   * promises, and what somebody topping a tester up actually wants. Idempotency exists here for
   * RETRIES (a webhook, a dropped response), not to second-guess a person pressing a button twice
   * on purpose.
   */
  async function topUp(row: AccountRow) {
    /*
     * ⚠ AN ACCOUNT THAT ALREADY HAS A CURRENCY KEEPS IT. Sending the picker's choice at a wallet
     * already in another currency would be refused server-side anyway — this just makes the
     * refusal impossible rather than handled, so the button never produces an error it could have
     * avoided by reading the row it is on.
     */
    const cur = (row.currency as 'ISK' | 'USD' | null) ?? currency
    setBusy(row.id)
    try {
      await api.topUp(row.id, AMOUNT[cur], cur, 'testing budget')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function setApproval(row: AccountRow, approved: boolean) {
    setBusy(row.id)
    setError('')
    try {
      await api.setApproval(row.id, approved)
      // The server owns the real timestamp; this is only so the row stops
      // looking stale before the next load.
      const approvedAt = approved ? (row.approvedAt ?? new Date().toISOString()) : null
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === row.id ? { ...r, approvedAt } : r)) : prev,
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not take.')
    } finally {
      setBusy(null)
    }
  }

  const playing = rows?.filter((r) => r.approvedAt !== null).length ?? 0

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <div className={styles.head}>
          {/*
            ⚠ STRIPE'S PLACEHOLDER, AND THE ONE DECISION IT CANNOT MAKE FOR YOU. Money enters by
            hand while the founder pays the testing budget; everything downstream of it is the real
            mechanism. The currency, though, is permanent per account — so it is picked, never
            defaulted silently.
          */}
          <label className={styles.currency}>
            top up in
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'ISK' | 'USD')}
            >
              <option value="ISK">{label('ISK')} · krónur</option>
              <option value="USD">{label('USD')} · dollars</option>
            </select>
          </label>
          <h1 className={styles.title}>Accounts</h1>
          <Link to="/" className={styles.back}>
            ‹ BACK
          </Link>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {rows === null ? (
          <p className={styles.empty}>reading…</p>
        ) : rows.length === 0 ? (
          <p className={styles.empty}>No one has signed up yet.</p>
        ) : (
          <>
            <p className={styles.count}>
              {rows.length} account{rows.length === 1 ? '' : 's'} · {playing} may
              play
            </p>
            {rows.map((r) => {
              const approved = r.approvedAt !== null
              return (
                <div key={r.id} className={styles.row}>
                  <div className={styles.who}>
                    <div className={styles.email}>{r.email}</div>
                    {r.note && <div className={styles.note}>“{r.note}”</div>}
                    <div className={styles.when}>
                      {r.provider && (
                        <span className={styles.provider}>{r.provider}</span>
                      )}
                      {r.provider && ' · '}
                      {new Date(r.createdAt).toLocaleDateString()}
                      {r.isAdmin && ' · admin'}
                      {/* Which price list they are on. Absent until their first top-up, and
                          permanent after — so it is worth seeing before you press anything. */}
                      {r.currency && ` · ${r.currency}`}
                    </div>
                  </div>

                  <div
                    className={`${styles.status} ${approved ? styles.approved : ''}`}
                  >
                    {approved ? 'approved' : 'member'}
                  </div>

                  <button
                    className={styles.action}
                    disabled={busy === r.id}
                    onClick={() => void setApproval(r, !approved)}
                  >
                    {approved ? 'take back' : 'let in'}
                  </button>

                  {/*
                    ⚠ STRIPE'S PLACEHOLDER (2026-08-27). The founder is paying the testing budget,
                    so money enters by hand — and everything downstream of it is the real
                    mechanism on real rows: the hold, the settlement, the remainder coming back.
                    When cards are wired this control goes and a verified webhook writes the same
                    `topup` row.
                  */}
                  <button
                    className={styles.action}
                    disabled={busy === r.id}
                    onClick={() => void topUp(r)}
                    title="add to this account's wallet"
                  >
                    {/* The label states the currency it will actually use — the row's own if it
                        has one, the picker's if it does not. A button that says "+ $5" and grants
                        krónur is a button that has to be checked afterwards. */}
                    + {label((r.currency as 'ISK' | 'USD' | null) ?? currency)}
                  </button>
                </div>
              )
            })}
          </>
        )}
      </div>
    </div>
  )
}
